import React, { useEffect, useRef, useState } from "react";
import { translations as translationCatalog } from "../../i18n/translations";

export interface FormValidationError {
  id: string;
  label: string;
  message?: string;
}

interface FormValidationTranslations {
  formValidationSummaryTitle: string;
  formValidationSummaryBody: string;
  formValidationRequiredMessage: string;
  formValidationEmailMessage: string;
  formValidationUrlMessage: string;
}

interface UseFormValidationOptions {
  t: FormValidationTranslations;
  fieldLabels?: Record<string, string>;
  includeNativeValidation?: boolean;
}

const getValidationKey = (
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
) => {
  if (element instanceof HTMLInputElement && element.type === "radio") {
    return element.name || element.id;
  }

  return element.id || element.name;
};

const normalizeFieldLabel = (label: string) =>
  label
    .replace(/\s+/g, " ")
    .replace(/^\*\s*/, "")
    .replace(/\s*\((required|obligatoire)\)\s*$/i, "")
    .trim();

const getFieldLabel = (
  form: HTMLFormElement,
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  fieldLabels: Record<string, string>
) => {
  const labelKey = getValidationKey(element);
  if (fieldLabels[element.id]) return normalizeFieldLabel(fieldLabels[element.id]);
  if (fieldLabels[element.name]) return normalizeFieldLabel(fieldLabels[element.name]);
  if (fieldLabels[labelKey]) return normalizeFieldLabel(fieldLabels[labelKey]);

  const explicitLabel = element.id
    ? form.querySelector(`label[for="${CSS.escape(element.id)}"]`)
    : null;
  const legend = element.closest("fieldset")?.querySelector("legend");
  const labelledBy = element.getAttribute("aria-labelledby");
  const ariaLabel = labelledBy
    ? labelledBy
        .split(/\s+/)
        .map((id) => form.querySelector(`#${CSS.escape(id)}`)?.textContent)
        .filter(Boolean)
        .join(" ")
    : "";

  if (element instanceof HTMLInputElement && element.type === "radio") {
    return normalizeFieldLabel(
      legend?.textContent?.trim() ||
        ariaLabel.trim() ||
        explicitLabel?.textContent?.trim() ||
        element.name ||
        element.id
    );
  }

  return normalizeFieldLabel(
    explicitLabel?.textContent?.trim() ||
    ariaLabel.trim() ||
    legend?.textContent?.trim() ||
    element.name ||
    element.id
  );
};

const getFieldMessage = (
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  t: FormValidationTranslations
) => {
  if (element.validity.typeMismatch && element instanceof HTMLInputElement) {
    return t.formValidationEmailMessage;
  }

  return t.formValidationRequiredMessage;
};

const getLocalizedValidationMessage = (
  message: string,
  t: FormValidationTranslations
) => {
  if (
    message === translationCatalog.en.formValidationRequiredMessage ||
    message === translationCatalog.fr.formValidationRequiredMessage
  ) {
    return t.formValidationRequiredMessage;
  }

  if (
    message === translationCatalog.en.formValidationEmailMessage ||
    message === translationCatalog.fr.formValidationEmailMessage
  ) {
    return t.formValidationEmailMessage;
  }

  if (
    message === translationCatalog.en.formValidationUrlMessage ||
    message === translationCatalog.fr.formValidationUrlMessage
  ) {
    return t.formValidationUrlMessage;
  }

  return message;
};

const refreshValidationMessage = (
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  message: string | undefined,
  t: FormValidationTranslations
) => {
  if (!message) return getFieldMessage(element, t);

  return getLocalizedValidationMessage(message, t);
};

const getErrorInsertionTarget = (element: HTMLElement) => {
  if (
    element instanceof HTMLInputElement &&
    (element.type === "radio" || element.type === "checkbox")
  ) {
    return element.closest("fieldset") || element;
  }

  return element;
};

export const useFormValidation = ({
  t,
  fieldLabels = {},
  includeNativeValidation = true,
}: UseFormValidationOptions) => {
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummaryRef = useRef<HTMLElement>(null);
  const [errors, setErrors] = useState<FormValidationError[]>([]);

  const collectNativeErrors = (form: HTMLFormElement) => {
    if (!includeNativeValidation) return [];

    const invalidElements = Array.from(
      form.querySelectorAll(":invalid")
    ) as Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
    const seenKeys = new Set<string>();

    return invalidElements.reduce<FormValidationError[]>((currentErrors, element) => {
      const key = getValidationKey(element);
      if (!key || seenKeys.has(key)) return currentErrors;

      seenKeys.add(key);
      currentErrors.push({
        id: element.id,
        label: getFieldLabel(form, element, fieldLabels),
        message: getFieldMessage(element, t),
      });

      return currentErrors;
    }, []);
  };

  const validateForm = (
    form: HTMLFormElement,
    customErrors: FormValidationError[] = []
  ) => {
    const nextErrors = [...collectNativeErrors(form), ...customErrors];
    const dedupedErrors = nextErrors.filter(
      (error, index, allErrors) =>
        error.id && allErrors.findIndex((item) => item.id === error.id) === index
    );

    setErrors(dedupedErrors);

    if (dedupedErrors.length > 0) {
      setTimeout(() => {
        errorSummaryRef.current?.focus();
      }, 0);
      return false;
    }

    return true;
  };

  const handleSubmit = (
    event: React.FormEvent,
    onValidSubmit: () => void,
    customErrors: FormValidationError[] = []
  ) => {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    if (!validateForm(form, customErrors)) return;

    onValidSubmit();
  };

  const handleChangeCapture = () => {
    window.setTimeout(() => {
      const form = formRef.current;
      if (!form || errors.length === 0) return;

      const remainingErrors = errors.filter((error) => {
        const element = form.querySelector(`#${CSS.escape(error.id)}`) as
          | HTMLInputElement
          | HTMLSelectElement
          | HTMLTextAreaElement
          | null;

        return element ? !element.checkValidity() : false;
      });

      if (remainingErrors.length !== errors.length) {
        setErrors(remainingErrors);
      }
    }, 0);
  };

  useEffect(() => {
    const form = formRef.current;
    if (!form || errors.length === 0) return;

    const refreshedErrors = errors.map((error) => {
      const element = form.querySelector(`#${CSS.escape(error.id)}`) as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;

      if (!element) return error;

      const refreshedLabel = getFieldLabel(form, element, fieldLabels);
      const refreshedMessage = refreshValidationMessage(
        element,
        error.message,
        t
      );

      return {
        ...error,
        label: refreshedLabel,
        message: refreshedMessage,
      };
    });

    const hasChanged = refreshedErrors.some(
      (refreshedError, index) =>
        refreshedError.label !== errors[index].label ||
        refreshedError.message !== errors[index].message
    );

    if (hasChanged) {
      setErrors(refreshedErrors);
    }
  }, [t, fieldLabels, errors]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const mountedErrors = errors.filter((error) =>
      form.querySelector(`#${CSS.escape(error.id)}`)
    );

    if (mountedErrors.length !== errors.length) {
      setErrors(mountedErrors);
      return;
    }

    const previousMessages = form.querySelectorAll(
      "[data-form-validation-error='true']"
    );
    previousMessages.forEach((message) => message.remove());

    const controls = form.querySelectorAll("input, select, textarea");
    controls.forEach((control) => {
      control.removeAttribute("aria-invalid");
      const describedBy = control.getAttribute("aria-describedby");
      if (describedBy) {
        const nextDescribedBy = describedBy
          .split(/\s+/)
          .filter((id) => !id.endsWith("-validation-error"))
          .join(" ");
        if (nextDescribedBy) {
          control.setAttribute("aria-describedby", nextDescribedBy);
        } else {
          control.removeAttribute("aria-describedby");
        }
      }
    });

    errors.forEach((error) => {
      const element = form.querySelector(`#${CSS.escape(error.id)}`) as HTMLElement | null;
      if (!element) return;

      const errorId = `${error.id}-validation-error`;
      const describedBy = element.getAttribute("aria-describedby") || "";
      const describedByIds = describedBy.split(/\s+/).filter(Boolean);

      element.setAttribute("aria-invalid", "true");
      if (!describedByIds.includes(errorId)) {
        element.setAttribute("aria-describedby", [...describedByIds, errorId].join(" "));
      }

      const message = document.createElement("p");
      message.id = errorId;
      message.className = "text-danger mrgn-tp-sm";
      message.setAttribute("data-form-validation-error", "true");
      message.textContent = error.message || t.formValidationRequiredMessage;

      const insertionTarget = getErrorInsertionTarget(element);
      insertionTarget.insertAdjacentElement("afterend", message);
    });
  }, [errors, t.formValidationRequiredMessage]);

  return {
    formRef,
    errorSummaryRef,
    errors,
    handleSubmit,
    handleChangeCapture,
    validateForm,
  };
};

interface FormValidationSummaryProps {
  t: FormValidationTranslations;
  errors: FormValidationError[];
  errorSummaryRef: React.RefObject<HTMLElement | null>;
  formRef: React.RefObject<HTMLFormElement | null>;
}

export const FormValidationSummary: React.FC<FormValidationSummaryProps> = ({
  t,
  errors,
  errorSummaryRef,
  formRef,
}) => {
  if (errors.length === 0) return null;

  return (
    <section
      ref={errorSummaryRef}
      className="alert alert-danger"
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
    >
      <h2 className="h4 mrgn-tp-0">{t.formValidationSummaryTitle}</h2>
      <p>{t.formValidationSummaryBody}</p>
      <ul className="mrgn-bttm-0">
        {errors.map((error) => (
          <li key={error.id}>
            <a
              href={`#${error.id}`}
              className="text-[#295376] underline hover:text-[#0535d2] focus:text-[#0535d2] visited:text-[#7834bc]"
              onClick={(event) => {
                event.preventDefault();
                const element = formRef.current?.querySelector(
                  `#${CSS.escape(error.id)}`
                ) as HTMLElement | null;
                element?.focus();
              }}
            >
              {error.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};